import { h } from 'preact';
import { useState } from 'preact/hooks';
import { t, translateError } from '../../i18n.js';
import { api } from '../../state/auth.js';
import Button from './Button.jsx';
import RoleDepartmentPicker, { RoleDepartmentsText } from './RoleDepartmentPicker.jsx';
import { departments as departmentsSignal, departmentChoices, loadedDepartments } from '../../utils/departments.js';

// The roles of a member on the members page: give and take a role, and (two or more
// departments) for which afdelingen it applies. roles: the club's roles, with
// club_wide_only for a role with admin or member-management rights.
export default function MemberRoles({ user, roles, canUpdate, onChanged, onError }) {
  const [assignRoleId, setAssignRoleId] = useState('');
  // For which departments the role being given applies, and the role whose departments
  // are being changed ({ roleId, ids })
  const [assignDepartmentIds, setAssignDepartmentIds] = useState([]);
  const [editingDepartments, setEditingDepartments] = useState(null);
  const choices = departmentChoices();
  // Reading the signal re-renders once the departments are in; null until then
  void departmentsSignal.value;
  const allDepartments = loadedDepartments();
  const fail = err => onError(translateError(err.code));

  function removeRole(roleId) {
    api(`/api/users/${user.id}/roles/${roleId}`, { method: 'DELETE' }).then(onChanged).catch(fail);
  }

  function assignRole() {
    if (!assignRoleId) return;
    const clubWideOnly = roles.find(r => String(r.id) === String(assignRoleId))?.club_wide_only;
    api(`/api/users/${user.id}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleId: assignRoleId, departmentIds: clubWideOnly ? [] : assignDepartmentIds }),
    })
      .then(() => { setAssignRoleId(''); setAssignDepartmentIds([]); onChanged(); })
      .catch(fail);
  }

  function saveDepartments() {
    api(`/api/users/${user.id}/roles/${editingDepartments.roleId}`, {
      method: 'PUT',
      body: JSON.stringify({ departmentIds: editingDepartments.ids }),
    })
      .then(() => { setEditingDepartments(null); onChanged(); })
      .catch(fail);
  }

  return (
    <>
    <div style="display:flex;flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:10px">
      {(user.roles || []).length === 0
        ? <span style="font-size:13px;color:var(--color-text-muted)">Geen rollen toegewezen.</span>
        : user.roles.map(role => {
          const clubWideOnly = roles.find(r => r.id === role.id)?.club_wide_only;
          const editing = editingDepartments?.roleId === role.id;
          return (
            <div key={role.id} style="display:flex;flex-direction:column;gap:6px">
              <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
                <span class="badge badge-default">{role.name}</span>
                <RoleDepartmentsText role={role} departments={allDepartments} />
                {canUpdate && choices.length > 0 && !clubWideOnly && !editing && (
                  <button type="button" style="background:none;border:none;padding:0;cursor:pointer;font-size:12px;color:var(--color-primary);text-decoration:underline"
                    onClick={() => setEditingDepartments({ roleId: role.id, ids: role.department_ids || [] })}>
                    Afdelingen
                  </button>
                )}
                {canUpdate && (
                  <button
                    type="button"
                    onClick={() => removeRole(role.id)}
                    style="background:none;border:none;cursor:pointer;color:var(--color-danger,#dc2626);font-size:16px;padding:0 2px;line-height:1"
                    title="Rol verwijderen"
                  >×</button>
                )}
              </div>
              {editing && (
                <div style="display:flex;flex-direction:column;gap:8px;padding:8px 10px;border:1px solid var(--color-border);border-radius:8px">
                  <RoleDepartmentPicker departments={choices} value={editingDepartments.ids}
                    onChange={ids => setEditingDepartments({ ...editingDepartments, ids })} />
                  <div class="flex gap-2">
                    <Button size="sm" onClick={() => saveDepartments()}>Opslaan</Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditingDepartments(null)}>Annuleren</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
    {canUpdate && (
      <div style="display:flex;flex-direction:column;gap:8px">
        <div class="flex gap-2">
          <select
            class="form-select"
            value={assignRoleId}
            onChange={e => setAssignRoleId(e.target.value)}
          >
            <option value="">{t('user.selectRole')}</option>
            {roles
              .filter(r => !user.roles?.some(ur => ur.id === r.id))
              .map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Button size="sm" onClick={() => assignRole()} disabled={!assignRoleId}>
            {t('user.assignRole')}
          </Button>
        </div>
        {assignRoleId && choices.length > 0 && (
          roles.find(r => String(r.id) === String(assignRoleId))?.club_wide_only
            ? <span style="font-size:12px;color:var(--color-text-muted)">Een rol met beheer- of ledenrechten geldt altijd voor de hele club.</span>
            : <RoleDepartmentPicker departments={choices} value={assignDepartmentIds} onChange={setAssignDepartmentIds} />
        )}
      </div>
    )}
    </>
  );
}
